import React, { ChangeEvent, useState } from "react";
import * as Yup from "yup";
import { observer } from "mobx-react";
import { useFormik } from "formik";
import {
  Button,
  Spinner,
  Text,
  VStack,
  Input,
  Field,
  NativeSelect,
  Stack,
  ButtonGroup,
} from "@chakra-ui/react";
import { t } from "i18next";
import { delay } from "@keita/lanepatrol-sdk";
import {
  DeviceOutputLinksResponse,
  GpxUploadLinksResponse,
  UploadInputType,
} from "../../../../../osaway/models/OsaWay";
import { OsaWayApi } from "../../../../../osaway/services/OsaWayApi";
import { OsaWayUploadProcessStatus } from "../../../../../osaway/models/OsaNode";
import { useStores } from "../../../../../shared/stores/RootStore";
import { toast } from "../../../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../../../shared/views/CustomDialog";

export enum SupportedFileFormats {
  gpx = "gpx",
  zip = "zip",
}

export interface NodeFormData {
  fileFormat?: SupportedFileFormats;
  zipOutputsFile?: File;
  zipPicturesFile?: File;
  gpxFile?: File;
  gpxPicturesFile?: File;
}

const UploadNodesBase = ({ onCancel }: { onCancel: () => void }) => {
  const { osaWayStore, osaNodeStore } = useStores();
  const [spinnerText, setSpinnerText] = useState<string>();

  const selectedWay = osaWayStore.getSelected()!;

  const initialValues: NodeFormData = {
    fileFormat: SupportedFileFormats.zip,
  };

  const formik = useFormik({
    initialValues: initialValues,

    onSubmit: async (values: NodeFormData) => {
      createFromPhotos(values);
    },
    validationSchema: CreateNodesSchema,
  });

  const createFromPhotos = async (formValues: NodeFormData) => {
    const showError = () => {
      setSpinnerText(undefined);
      toast("error", t("osaway:photos.upload.uploadError"));
    };

    const osaWayId = selectedWay.id;

    setSpinnerText(t("osaway:photos.upload.uploading"));
    const inputType =
      formValues.fileFormat === SupportedFileFormats.zip
        ? UploadInputType.DeviceOutput
        : UploadInputType.Gpx;
    const uploadLinks = await OsaWayApi.getZipUploadLinks(osaWayId, {
      inputType,
    })
      .then((response) => response.data)
      .catch(() => showError());
    if (!uploadLinks) return;

    if (formValues.fileFormat === SupportedFileFormats.zip) {
      const deviceOutputUploadLinks = uploadLinks as DeviceOutputLinksResponse;
      const outputsPutResponse = await fetch(deviceOutputUploadLinks.outputs, {
        method: "PUT",
        headers: new Headers({ "Content-Type": "application/zip" }),
        body: formValues.zipOutputsFile,
      });
      if (outputsPutResponse.status !== 200) return showError();
      const picturesPutResponse = await fetch(
        deviceOutputUploadLinks.pictures,
        {
          method: "PUT",
          headers: new Headers({ "Content-Type": "application/zip" }),
          body: formValues.zipPicturesFile,
        }
      );
      if (picturesPutResponse.status !== 200) return showError();
    } else if (formValues.fileFormat === SupportedFileFormats.gpx) {
      const gpxUploadLinks = uploadLinks as GpxUploadLinksResponse;
      const outputsPutResponse = await fetch(gpxUploadLinks.gpx, {
        method: "PUT",
        headers: new Headers({ "Content-Type": "text/xml" }),
        body: formValues.gpxFile,
      });
      if (outputsPutResponse.status !== 200) return showError();
      const picturesPutResponse = await fetch(gpxUploadLinks.pictures, {
        method: "PUT",
        headers: new Headers({ "Content-Type": "application/zip" }),
        body: formValues.gpxPicturesFile,
      });
      if (picturesPutResponse.status !== 200) return showError();
    }

    setSpinnerText(t("osaway:photos.upload.processing"));
    var response = (
      await OsaWayApi.startReadingUploadedFiles(osaWayId, inputType)
    ).data;

    while (response.status === OsaWayUploadProcessStatus.IN_PROGRESS) {
      await delay(2500);
      response = (await OsaWayApi.getUploadFilesStatus(osaWayId)).data;
      setSpinnerText(
        t("osaway:photos.upload.photosProcessed", {
          count: response.progress || 0,
        })
      );
    }
    if (response.status === OsaWayUploadProcessStatus.FAILED)
      return showError();

    setSpinnerText(undefined);
    toast("success", t("osaway:photos.upload.uploadSuccess"));
    osaWayStore.refreshOsaWay(osaWayId);
    osaNodeStore.fetchNodes(osaWayId);
  };

  return (
    <Stack>
      <form id="uploadNodes" onSubmit={formik.handleSubmit}>
        <Field.Root fontSize={"sm"}>
          <Field.Label mt={4}>
            {t("osaway:photos.upload.formatLabel")}
          </Field.Label>
          <NativeSelect.Root variant="outline">
            <NativeSelect.Field
              name="fileFormat"
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                formik.handleChange(e);
                // clear files from state because the file inputs will be unmounted/reset.
                formik.setFieldValue("zipOutputsFile", undefined);
                formik.setFieldValue("zipPicturesFile", undefined);
                formik.setFieldValue("gpxFile", undefined);
              }}
              value={formik.values.fileFormat}
            >
              {formatOption(SupportedFileFormats.zip)}
              {formatOption(SupportedFileFormats.gpx)}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>

        {formik.values.fileFormat === SupportedFileFormats.zip && (
          <>
            <Field.Root flexBasis={1} flexGrow={1} fontSize={"sm"}>
              <Field.Label mt={4}>
                {t("osaway:photos.upload.titleZipOutputs")}
              </Field.Label>
              <Input
                required
                name="zipOutputsFile"
                variant="flushed"
                type="file"
                accept=".zip"
                onChange={(e: any) => {
                  formik.setFieldValue("zipOutputsFile", e.target.files[0]);
                }}
              />
            </Field.Root>
            <Field.Root flexBasis={1} flexGrow={1} fontSize={"sm"}>
              <Field.Label mt={4}>
                {t("osaway:photos.upload.titleZipPictures")}
              </Field.Label>
              <Input
                required
                name="zipPicturesFile"
                variant="flushed"
                type="file"
                accept=".zip"
                onChange={(e: any) => {
                  formik.setFieldValue("zipPicturesFile", e.target.files[0]);
                }}
              />
            </Field.Root>
          </>
        )}

        {formik.values.fileFormat === SupportedFileFormats.gpx && (
          <>
            <Field.Root fontSize={"sm"}>
              <Field.Label mt={4}>
                {t("osaway:photos.upload.titleGpx")}
              </Field.Label>
              <Input
                required
                name="gpxFile"
                variant="flushed"
                type="file"
                accept=".gpx"
                onChange={(e: any) => {
                  formik.setFieldValue("gpxFile", e.target.files[0]);
                }}
              />
            </Field.Root>
            <Field.Root fontSize={"sm"}>
              <Field.Label mt={4}>
                {t("osaway:photos.upload.titleGpxPictures")}
              </Field.Label>
              <Input
                required
                name="gpxPicturesFile"
                variant="flushed"
                type="file"
                accept=".zip"
                onChange={(e: any) => {
                  formik.setFieldValue("gpxPicturesFile", e.target.files[0]);
                }}
              />
            </Field.Root>
          </>
        )}
      </form>

      <ButtonGroup
        mt={4}
        justify="end"
        colorPalette="primary"
        size="sm"
        fontSize={"sm"}
      >
        <Button variant={"ghost"} onClick={onCancel}>
          {t("shared:cancel")}
        </Button>
        <Button
          type="submit"
          form="uploadNodes"
          disabled={!formik.isValid || !formik.dirty}
        >
          {t("shared:project.submit.done")}
        </Button>
      </ButtonGroup>

      {!!spinnerText && <ProgressModal text={spinnerText}></ProgressModal>}
    </Stack>
  );
};

export const UploadNodes = observer(UploadNodesBase);

interface ProgressModalProps {
  text: string;
}
const ProgressModal: React.FC<ProgressModalProps> = ({ text }) => {
  return (
    <CustomDialog size="md" header={null} onClose={() => {}}>
      <VStack py={6}>
        <Spinner size="lg" />
        <Text mt={4}>{text}</Text>
      </VStack>
    </CustomDialog>
  );
};

const formatOption = (format: SupportedFileFormats) => (
  <option value={format}>{t(`shared:route.formats.${format}`)}</option>
);

export const CreateNodesSchema = Yup.object().shape({
  fileFormat: Yup.string().required(),
  zipOutputsFile: Yup.mixed().when("fileFormat", {
    is: (value: string) => SupportedFileFormats.zip === value,
    then: Yup.mixed().required(),
  }),
  zipPicturesFile: Yup.mixed().when("fileFormat", {
    is: (value: string) => SupportedFileFormats.zip === value,
    then: Yup.mixed().required(),
  }),
  gpxFile: Yup.mixed().when("fileFormat", {
    is: (value: string) => SupportedFileFormats.gpx === value,
    then: Yup.mixed().required(),
  }),
  gpxPicturesFile: Yup.mixed().when("fileFormat", {
    is: (value: string) => SupportedFileFormats.gpx === value,
    then: Yup.mixed().required(),
  }),
});
